import clsx from 'clsx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { findLink } from '../../LinkyLinky';
import NotesStore from '../../NotesStore';
import ProgramStore from '../../ProgramStore';
import { whenUpcoming } from '../../ProgramStore/Types';
import SegmentStore from '../../SegmentStore';
import ProgramSegmentReactions from '../../SegmentStore/ProgramSegmentReactions';
import { T } from '../../Translations';
import { useTranslationsContext } from '../../Translations/UseTranslationsContext';
import NotesTabIcon from '../Cutesies/NotesTabIcon';
import OverviewTabIcon from '../Cutesies/OverviewTabIcon';
import ResourcesTabIcon from '../Cutesies/ResourcesTabIcon';
import PositionContext from '../PositionContext';
import NotesTabPane from './NotesTabPane';
import OutlineTabPane from './OutlineTabPane';
import ProgressRing from './ProgressRing';
import ProgressSection from './ProgressSection';
import ResourceTabPane from './ResourceTabPane';
import ToolButton from './ToolButton';
import * as style from './style.module.css';

interface Props {
  segmentStore: SegmentStore;
  programStore: ProgramStore;
}

type Tool = 'none' | 'progress' | 'notes' | 'resources' | 'outline';

const POPUP_ID = 'segment-tools-popup';
const VIEWPORT_MARGIN = 16;
const POPUP_MIN_HEIGHT = 200;

/**
 * The course tools (progress, notes, resources and the program overview) as a row of
 * icon buttons beside the segment title. Each button opens its pane in one popup, which
 * becomes a bottom sheet on small screens. Every pane stays mounted while closed, so the
 * notes load (and the badge counts them) before the learner opens the popup, and a note
 * being written survives closing it.
 */
function SegmentPanel({ segmentStore, programStore }: Props) {
  const { translate } = useTranslationsContext();
  const [notesStore] = React.useState(() => new NotesStore());
  const [openTool, setOpenTool] = React.useState<Tool>('none');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const popupRef = React.useRef<HTMLElement>(null);
  const [popupMaxHeight, setPopupMaxHeight] = React.useState<number | undefined>();

  const hasNotes = findLink('notes', segmentStore.links).isJust();
  const hasResources = findLink('resource-template', segmentStore.links).isJust();
  // A program that has not started has no progress to show yet.
  const hasProgress = programStore.programDetailResource
    .map((resource) => whenUpcoming(resource.payload).isNothing())
    .getOrElseValue(true);
  const segmentId = segmentStore.segmentResource.map((r) => r.payload.id).getOrElseValue(0);
  const percentComplete = Math.round(programStore.percentComplete.getOrElseValue(0));

  const close = React.useCallback(() => {
    setOpenTool('none');
  }, []);
  const toggle = (tool: Tool) => () => {
    setOpenTool((current) => (current === tool ? 'none' : tool));
  };

  // Moving to another segment (including from the overview) closes the popup.
  React.useEffect(close, [segmentId, close]);

  React.useEffect(() => {
    if (openTool === 'none') {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openTool, close]);

  // On wider screens the popup hangs below the toolbar, whose position depends on the logo
  // and on how far the page is scrolled. Cap the popup at the space left below its top edge
  // so it never runs off the bottom of the viewport. Small screens use a bottom sheet with
  // its own fixed cap.
  React.useLayoutEffect(() => {
    if (openTool === 'none') {
      return;
    }
    const fit = () => {
      const popup = popupRef.current;
      if (!popup || !globalThis.matchMedia('(min-width: 640px)').matches) {
        setPopupMaxHeight(undefined);
        return;
      }
      const top = popup.getBoundingClientRect().top;
      setPopupMaxHeight(Math.max(POPUP_MIN_HEIGHT, window.innerHeight - top - VIEWPORT_MARGIN));
    };
    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('scroll', fit, { passive: true });
    return () => {
      window.removeEventListener('resize', fit);
      window.removeEventListener('scroll', fit);
    };
  }, [openTool]);

  const pane = (tool: Tool, content: React.ReactNode) => (
    <div hidden={openTool !== tool} data-test-segment-tool-pane={tool}>
      {content}
    </div>
  );

  return (
    <PositionContext.Provider value={{ kind: 'supPanel' }}>
      <div ref={containerRef} className="relative shrink-0">
        <div
          role="toolbar"
          aria-label={translate('Segment Panel')}
          className="flex justify-between gap-1 rounded-xl bg-gray-100 p-1 sm:justify-start"
          data-test-tabs={true}
        >
          {hasProgress && (
            <ToolButton
              label={<T kind="Progress" />}
              icon={<ProgressRing percentComplete={percentComplete} />}
              badgeCount={0}
              active={openTool === 'progress'}
              controls={POPUP_ID}
              onClick={toggle('progress')}
              intercomTarget="segment-tools-progress-button"
              dataTest={{ 'data-test-progress-tab': true }}
            />
          )}
          {hasNotes && (
            <ToolButton
              label={<T kind="Notes" />}
              icon={
                <span className="block h-5 w-5">
                  <NotesTabIcon />
                </span>
              }
              badgeCount={notesStore.noteStores.length}
              active={openTool === 'notes'}
              controls={POPUP_ID}
              onClick={toggle('notes')}
              intercomTarget="segment-tools-notes-button"
              dataTest={{ 'data-test-notes-tab': true }}
            />
          )}
          {hasResources && (
            <ToolButton
              label={<T kind="Resources" />}
              icon={
                <span className="block h-5 w-5">
                  <ResourcesTabIcon />
                </span>
              }
              badgeCount={segmentStore.attachments.length}
              active={openTool === 'resources'}
              controls={POPUP_ID}
              onClick={toggle('resources')}
              intercomTarget="segment-tools-resources-button"
              dataTest={{ 'data-test-resources-tab': true }}
            />
          )}
          <ToolButton
            label={<T kind="Overview" />}
            icon={
              <span className="block h-5 w-5">
                <OverviewTabIcon />
              </span>
            }
            badgeCount={0}
            active={openTool === 'outline'}
            controls={POPUP_ID}
            onClick={toggle('outline')}
            intercomTarget="segment-tools-overview-button"
            dataTest={{ 'data-test-outline-tab': true }}
          />
        </div>

        {openTool !== 'none' && (
          <div
            className="fixed inset-0 z-40 bg-black/40 sm:hidden"
            aria-hidden="true"
            onClick={close}
          />
        )}
        <section
          id={POPUP_ID}
          ref={popupRef}
          style={popupMaxHeight === undefined ? undefined : { maxHeight: popupMaxHeight }}
          aria-label={translate('Segment Panel')}
          hidden={openTool === 'none'}
          className={clsx(
            style.popup,
            'fixed inset-x-0 bottom-0 z-50 box-border max-h-[75vh] overflow-y-auto rounded-t-2xl bg-white px-5 pb-5 pt-6 shadow-2xl',
            'sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[28rem] sm:rounded-xl',
          )}
          data-test-segment-tools-popup={true}
        >
          <button
            type="button"
            className="absolute right-3 top-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={translate('Close')}
            onClick={close}
            data-intercom-target="segment-tools-close-button"
          >
            <svg
              className="shrink-0 stroke-gray-500"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              aria-hidden="true"
              focusable="false"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M3 3l10 10M13 3L3 13" />
            </svg>
          </button>
          {hasProgress && pane('progress', <ProgressSection programStore={programStore} />)}
          {hasNotes &&
            pane(
              'notes',
              segmentStore.segmentResource
                .map((resource) => (
                  <NotesTabPane
                    segmentResource={resource}
                    segmentStore={segmentStore}
                    notesStore={notesStore}
                  />
                ))
                .getOrElseValue(<></>),
            )}
          {hasResources && pane('resources', <ResourceTabPane store={segmentStore} />)}
          {pane(
            'outline',
            <OutlineTabPane programStore={programStore} segmentStore={segmentStore} />,
          )}
        </section>
      </div>
      <ProgramSegmentReactions
        fireImmediately={true}
        store={segmentStore}
        programStore={programStore}
      />
    </PositionContext.Provider>
  );
}

export default observer(SegmentPanel);
